import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  User, 
  Phone, 
  Building2, 
  Shield, 
  Key, 
  Save, 
  Loader2,
  Eye,
  EyeOff,
  CheckCircle2,
  Mic,
  Volume2,
  RefreshCw,
  Camera,
  Trash2,
  Upload,
  Home,
  Download,
  Bell,
  Monitor,
  Apple,
  Music,
  Play,
  Square,
  PhoneOff
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import usePushNotifications from '@/hooks/usePushNotifications';
import { Switch } from '@/components/ui/switch';
import { getSavedSipCredentials, clearSavedSipCredentials } from '@/components/SipSetupDialog';

import { API_BASE_URL } from '../utils/api';
const API_URL = API_BASE_URL;

// Simple obfuscation for local credential storage
const obfuscate = (str) => btoa(str.split('').reverse().join(''));
const SIP_CREDS_KEY = 'connect_sip_creds';

// Small form component for saving credentials
function SaveCredentialsForm({ user }) {
  const [sipPassword, setSipPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberCredentials, setRememberCredentials] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSave = () => {
    if (!sipPassword.trim()) {
      toast.error('Please enter your SIP password');
      return;
    }
    if (!rememberCredentials) {
      toast.error('Please check "Remember credentials" to save');
      return;
    }
    
    setSaving(true);
    try {
      localStorage.setItem(SIP_CREDS_KEY, JSON.stringify({
        phone: user?.phone_number,
        password: obfuscate(sipPassword)
      }));
      toast.success('Credentials saved locally!');
      setSipPassword('');
      setRememberCredentials(false);
      // Force re-render to update status
      window.location.reload();
    } catch (err) {
      toast.error('Failed to save credentials');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="sip-password-save">SIP Password</Label>
        <div className="relative">
          <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            id="sip-password-save"
            type={showPassword ? 'text' : 'password'}
            value={sipPassword}
            onChange={(e) => setSipPassword(e.target.value)}
            placeholder="Enter your SIP password"
            className="pl-10 pr-10"
            data-testid="sip-password-save-input"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>
      
      <div className="flex items-center space-x-2">
        <Checkbox
          id="remember-credentials-profile"
          checked={rememberCredentials}
          onCheckedChange={setRememberCredentials}
          data-testid="remember-credentials-profile-checkbox"
        />
        <Label htmlFor="remember-credentials-profile" className="text-sm font-normal cursor-pointer flex items-center gap-1.5">
          <Shield className="w-3.5 h-3.5 text-muted-foreground" />
          Remember my credentials on this device
        </Label>
      </div>
      
      {rememberCredentials && (
        <p className="text-xs text-amber-500/80 pl-6">
          Your SIP password will be stored locally. Only use on personal/trusted devices.
        </p>
      )}
      
      <Button 
        onClick={handleSave} 
        disabled={saving || !sipPassword || !rememberCredentials}
        variant="outline"
        data-testid="save-credentials-btn"
      >
        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
        Save Credentials
      </Button>
    </div>
  );
}

// Landing page options with backend feature IDs
const LANDING_PAGE_OPTIONS = [
  { value: 'dialer', label: 'My Phone', backendId: 'dialer' },
  { value: 'contacts', label: 'Contacts', backendId: 'contacts' },
  { value: 'my-calls', label: 'My Calls', backendId: 'my_calls' },
  { value: 'queues', label: 'Queue Monitor', backendId: 'queue_monitor' },
  { value: 'history', label: 'Call History', backendId: 'call_history' },
  { value: 'analytics', label: 'Analytics', backendId: 'analytics' },
];

// Built-in ringtone options
const RINGTONE_OPTIONS = [
  { id: 'none', label: 'No Ringtone (Silent)', freq1: 0, freq2: 0, pattern: 'none' },
  { id: 'classic', label: 'Classic Ring', freq1: 440, freq2: 480, pattern: 'double' },
  { id: 'modern', label: 'Modern Pulse', freq1: 523, freq2: 659, pattern: 'single' },
  { id: 'gentle', label: 'Gentle Chime', freq1: 587, freq2: 784, pattern: 'triple' },
  { id: 'urgent', label: 'Urgent', freq1: 880, freq2: 0, pattern: 'rapid' },
  { id: 'subtle', label: 'Subtle Tone', freq1: 392, freq2: 0, pattern: 'slow' },
];

export default function Profile() {
  const { user, token, refreshUser, hasFeatureAccess, isFeatureRestrictedByPlan } = useAuth();
  const fileInputRef = useRef(null);
  const ringtoneFileRef = useRef(null);
  const previewAudioRef = useRef(null);
  
  // Profile form state
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [profileLoading, setProfileLoading] = useState(false);
  const [userExtension, setUserExtension] = useState(null);
  
  // Profile image state
  const [profileImage, setProfileImage] = useState(user?.profile_image || null);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Password form state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  // Audio device state
  const [audioDevices, setAudioDevices] = useState({ inputs: [], outputs: [] });
  const [selectedMic, setSelectedMic] = useState('');
  const [selectedSpeaker, setSelectedSpeaker] = useState('');
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [testingAudio, setTestingAudio] = useState(false);
  
  // Ringtone state
  const [selectedRingtone, setSelectedRingtone] = useState(user?.ringtone_preference || 'classic');
  const [customRingtone, setCustomRingtone] = useState(user?.custom_ringtone || null);
  const [customRingtoneName, setCustomRingtoneName] = useState('');
  const [uploadingRingtone, setUploadingRingtone] = useState(false);
  const [previewingRingtone, setPreviewingRingtone] = useState(null);

  // Softphone preferences
  const [defaultContactFilter, setDefaultContactFilter] = useState('internal');
  const [defaultLandingPage, setDefaultLandingPage] = useState('dialer');
  const [dialerEnabled, setDialerEnabled] = useState(user?.dialer_enabled !== false);
  const [dialerToggleLoading, setDialerToggleLoading] = useState(false);
  
  // Push notifications
  const { isSupported: pushSupported, isSubscribed: pushSubscribed, permission: pushPermission, subscribe: subscribePush, unsubscribe: unsubscribePush, loading: pushLoading } = usePushNotifications();
  
  // Desktop app download
  const [desktopVersions, setDesktopVersions] = useState({ windows: null, mac: null });
  const [loadingDesktopVersions, setLoadingDesktopVersions] = useState(false);

  // Detect if running in Electron
  const isElectron = typeof window !== 'undefined' && window.electronAPI?.isElectron;

  // Fetch desktop app version info
  const fetchDesktopVersions = async () => {
    setLoadingDesktopVersions(true);
    try {
      const response = await axios.get(`${API_URL}/api/desktop/versions`);
      const versions = {};
      (response.data.versions || []).forEach(v => {
        versions[v.platform] = v;
      });
      setDesktopVersions(versions);
    } catch (error) {
      console.log('Could not fetch desktop versions');
    } finally {
      setLoadingDesktopVersions(false);
    }
  };

  // Load preferences on mount
  useEffect(() => {
    enumerateAudioDevices();
    fetchDesktopVersions();
    const savedFilter = localStorage.getItem('defaultContactFilter');
    if (savedFilter) setDefaultContactFilter(savedFilter);
    const savedLandingPage = localStorage.getItem('defaultLandingPage');
    if (savedLandingPage) setDefaultLandingPage(savedLandingPage);
    const savedRingtone = user?.ringtone_preference || localStorage.getItem('preferredRingtone') || 'classic';
    setSelectedRingtone(savedRingtone);
    if (user?.custom_ringtone) setCustomRingtone(user.custom_ringtone);
    if (user?.profile_image) setProfileImage(user.profile_image);
    if (token && user?.phone_number) fetchUserExtension();
  }, [user, token]);

  const fetchUserExtension = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/contacts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const myContact = res.data.find(c => c.phone_number === user.phone_number);
      if (myContact?.extension) setUserExtension(myContact.extension);
    } catch (error) {
      console.log('Could not fetch extension');
    }
  };

  // Handle image file selection
  const handleImageSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error('Image must be less than 2MB'); return; }

    setUploadingImage(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const imageData = e.target.result;
        await axios.put(`${API_URL}/api/auth/profile-image`, 
          { image_data: imageData },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setProfileImage(imageData);
        toast.success('Profile image updated');
        refreshUser();
      };
      reader.onerror = () => toast.error('Failed to read image file');
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDeleteImage = async () => {
    setUploadingImage(true);
    try {
      await axios.delete(`${API_URL}/api/auth/profile-image`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfileImage(null);
      toast.success('Profile image removed');
      refreshUser();
    } catch (error) {
      toast.error('Failed to remove image');
    } finally {
      setUploadingImage(false);
    }
  };

  // Enumerate available audio devices
  const enumerateAudioDevices = async () => {
    setLoadingDevices(true);
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true })
        .then(stream => { stream.getTracks().forEach(track => track.stop()); });

      const devices = await navigator.mediaDevices.enumerateDevices();
      
      const inputs = devices
        .filter(d => d.kind === 'audioinput')
        .map(d => ({ deviceId: d.deviceId, label: d.label || `Microphone ${d.deviceId.slice(0, 8)}...` }));
      
      const outputs = devices
        .filter(d => d.kind === 'audiooutput')
        .map(d => ({ deviceId: d.deviceId, label: d.label || `Speaker ${d.deviceId.slice(0, 8)}...` }));

      setAudioDevices({ inputs, outputs });

      // Load saved preferences - check localStorage first, then server-stored labels
      const savedMic = localStorage.getItem('preferredMicrophone');
      const savedMicLabel = localStorage.getItem('preferredMicrophoneLabel') || user?.preferred_mic_label;
      const savedSpeaker = localStorage.getItem('preferredSpeaker');
      const savedSpeakerLabel = localStorage.getItem('preferredSpeakerLabel') || user?.preferred_speaker_label;
      
      let selectedMicId = null;
      if (savedMic && inputs.some(d => d.deviceId === savedMic)) {
        selectedMicId = savedMic;
      } else if (savedMicLabel) {
        const matchByLabel = inputs.find(d => d.label === savedMicLabel);
        if (matchByLabel) {
          selectedMicId = matchByLabel.deviceId;
          localStorage.setItem('preferredMicrophone', selectedMicId);
        }
      }
      if (!selectedMicId && inputs.length > 0) {
        selectedMicId = inputs.find(d => d.deviceId === 'default')?.deviceId || inputs[0].deviceId;
      }
      if (selectedMicId) setSelectedMic(selectedMicId);
      
      let selectedSpeakerId = null;
      if (savedSpeaker && outputs.some(d => d.deviceId === savedSpeaker)) {
        selectedSpeakerId = savedSpeaker;
      } else if (savedSpeakerLabel) {
        const matchByLabel = outputs.find(d => d.label === savedSpeakerLabel);
        if (matchByLabel) {
          selectedSpeakerId = matchByLabel.deviceId;
          localStorage.setItem('preferredSpeaker', selectedSpeakerId);
        }
      }
      if (!selectedSpeakerId && outputs.length > 0) {
        selectedSpeakerId = outputs.find(d => d.deviceId === 'default')?.deviceId || outputs[0].deviceId;
      }
      if (selectedSpeakerId) setSelectedSpeaker(selectedSpeakerId);
    } catch (error) {
      console.error('Failed to enumerate devices:', error);
      toast.error('Could not access audio devices. Please allow microphone permission.');
    } finally {
      setLoadingDevices(false);
    }
  };

  // Save audio preferences
  const saveAudioPreferences = async () => {
    // Save device IDs to localStorage (for this browser/device)
    if (selectedMic) {
      localStorage.setItem('preferredMicrophone', selectedMic);
      const micDevice = audioDevices.inputs.find(d => d.deviceId === selectedMic);
      if (micDevice?.label) localStorage.setItem('preferredMicrophoneLabel', micDevice.label);
    }
    if (selectedSpeaker) {
      localStorage.setItem('preferredSpeaker', selectedSpeaker);
      const speakerDevice = audioDevices.outputs.find(d => d.deviceId === selectedSpeaker);
      if (speakerDevice?.label) localStorage.setItem('preferredSpeakerLabel', speakerDevice.label);
      window.dispatchEvent(new StorageEvent('storage', {
        key: 'preferredSpeaker',
        newValue: selectedSpeaker,
        oldValue: localStorage.getItem('preferredSpeaker')
      }));
    }
    
    // Save device LABELS to server (syncs across devices - labels are stable, IDs are not)
    try {
      const micLabel = audioDevices.inputs.find(d => d.deviceId === selectedMic)?.label || null;
      const speakerLabel = audioDevices.outputs.find(d => d.deviceId === selectedSpeaker)?.label || null;
      
      await axios.put(`${API_URL}/api/auth/audio-preferences`, {
        preferred_mic_label: micLabel,
        preferred_speaker_label: speakerLabel
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.warn('Could not sync audio preferences to server:', error);
    }
    
    // Save ringtone preference to server (syncs across devices)
    try {
      await axios.put(`${API_URL}/api/auth/ringtone`, {
        ringtone_id: selectedRingtone,
        custom_ringtone: selectedRingtone === 'custom' ? customRingtone : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      localStorage.setItem('preferredRingtone', selectedRingtone);
      if (customRingtone) localStorage.setItem('customRingtone', customRingtone);
      await refreshUser();
      toast.success('Audio settings saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save audio settings');
    }
  };

  // Save preference settings
  const savePreferences = () => {
    localStorage.setItem('defaultContactFilter', defaultContactFilter);
    localStorage.setItem('defaultLandingPage', defaultLandingPage);
    toast.success('Preferences saved');
  };

  // Test microphone
  const testMicrophone = async () => {
    setTestingAudio(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: selectedMic ? { exact: selectedMic } : undefined }
      });
      const audioContext = new AudioContext();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      source.connect(analyser);
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      let maxLevel = 0;
      const checkLevel = () => {
        analyser.getByteFrequencyData(dataArray);
        const level = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
        maxLevel = Math.max(maxLevel, level);
      };
      const interval = setInterval(checkLevel, 100);
      await new Promise(resolve => setTimeout(resolve, 2000));
      clearInterval(interval);
      stream.getTracks().forEach(track => track.stop());
      audioContext.close();
      if (maxLevel > 5) {
        toast.success('Microphone is working! Audio detected.');
      } else {
        toast.warning('Microphone connected but no audio detected. Try speaking.');
      }
    } catch (error) {
      toast.error('Could not test microphone: ' + error.message);
    } finally {
      setTestingAudio(false);
    }
  };

  // Test speaker
  const testSpeaker = async () => {
    setTestingAudio(true);
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const sampleRate = audioContext.sampleRate;
      const duration = 0.5;
      const frequency = 440;
      const numSamples = sampleRate * duration;
      const offlineContext = new OfflineAudioContext(1, numSamples, sampleRate);
      const oscillator = offlineContext.createOscillator();
      const gainNode = offlineContext.createGain();
      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';
      gainNode.gain.value = 0.3;
      oscillator.connect(gainNode);
      gainNode.connect(offlineContext.destination);
      oscillator.start(0);
      oscillator.stop(duration);
      const renderedBuffer = await offlineContext.startRendering();
      const wavBlob = audioBufferToWav(renderedBuffer);
      const audioUrl = URL.createObjectURL(wavBlob);
      const audio = new Audio(audioUrl);
      if (selectedSpeaker && typeof audio.setSinkId === 'function') {
        try { await audio.setSinkId(selectedSpeaker); } catch (e) { console.warn('Could not set speaker:', e); }
      }
      await audio.play();
      await new Promise(resolve => { audio.onended = resolve; setTimeout(resolve, 1000); });
      URL.revokeObjectURL(audioUrl);
      audioContext.close();
      toast.success('Did you hear the beep?');
    } catch (error) {
      toast.error('Could not test speaker: ' + error.message);
    } finally {
      setTestingAudio(false);
    }
  };

  const audioBufferToWav = (buffer) => {
    const numChannels = buffer.numberOfChannels;
    const sampleRate = buffer.sampleRate;
    const bitDepth = 16;
    const bytesPerSample = bitDepth / 8;
    const blockAlign = numChannels * bytesPerSample;
    const data = buffer.getChannelData(0);
    const dataLength = data.length * bytesPerSample;
    const totalLength = 44 + dataLength;
    const arrayBuffer = new ArrayBuffer(totalLength);
    const view = new DataView(arrayBuffer);
    const writeString = (offset, string) => {
      for (let i = 0; i < string.length; i++) view.setUint8(offset + i, string.charCodeAt(i));
    };
    writeString(0, 'RIFF');
    view.setUint32(4, totalLength - 8, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * blockAlign, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitDepth, true);
    writeString(36, 'data');
    view.setUint32(40, dataLength, true);
    let offset = 44;
    for (let i = 0; i < data.length; i++) {
      const sample = Math.max(-1, Math.min(1, data[i]));
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
      offset += 2;
    }
    return new Blob([arrayBuffer], { type: 'audio/wav' });
  };

  // Preview ringtone
  const previewRingtone = (ringtone) => {
    stopRingtonePreview();
    
    if (ringtone.id === 'none') return;
    
    // Custom ringtone - play the uploaded audio
    if (ringtone.id === 'custom' && customRingtone) {
      setPreviewingRingtone('custom');
      const audio = new Audio(customRingtone);
      previewAudioRef.current = audio;
      if (selectedSpeaker && typeof audio.setSinkId === 'function') {
        try { audio.setSinkId(selectedSpeaker); } catch (e) {}
      }
      audio.play().catch(() => {});
      audio.onended = () => setPreviewingRingtone(null);
      setTimeout(() => { 
        if (previewAudioRef.current === audio) {
          audio.pause(); 
          setPreviewingRingtone(null); 
        }
      }, 5000);
      return;
    }

    setPreviewingRingtone(ringtone.id);
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      previewAudioRef.current = ctx;
      
      const playTone = (startTime, freq, dur) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = freq;
        osc.type = 'sine';
        gain.gain.setValueAtTime(0, startTime);
        gain.gain.linearRampToValueAtTime(0.25, startTime + 0.03);
        gain.gain.linearRampToValueAtTime(0.25, startTime + dur - 0.05);
        gain.gain.linearRampToValueAtTime(0, startTime + dur);
        osc.start(startTime);
        osc.stop(startTime + dur);
      };

      const t = ctx.currentTime;
      if (ringtone.pattern === 'double') {
        playTone(t, ringtone.freq1, 0.4);
        if (ringtone.freq2) playTone(t, ringtone.freq2, 0.4);
        playTone(t + 0.5, ringtone.freq1, 0.4);
        if (ringtone.freq2) playTone(t + 0.5, ringtone.freq2, 0.4);
      } else if (ringtone.pattern === 'single') {
        playTone(t, ringtone.freq1, 0.6);
        if (ringtone.freq2) playTone(t + 0.2, ringtone.freq2, 0.4);
      } else if (ringtone.pattern === 'triple') {
        playTone(t, ringtone.freq1, 0.2);
        playTone(t + 0.25, ringtone.freq2 || ringtone.freq1, 0.2);
        playTone(t + 0.5, ringtone.freq1, 0.3);
      } else if (ringtone.pattern === 'rapid') {
        for (let i = 0; i < 4; i++) {
          playTone(t + i * 0.15, ringtone.freq1, 0.1);
        }
      } else if (ringtone.pattern === 'slow') {
        playTone(t, ringtone.freq1, 1.0);
      }

      setTimeout(() => {
        setPreviewingRingtone(null);
        if (previewAudioRef.current === ctx) {
          try { ctx.close(); } catch (e) {}
          previewAudioRef.current = null;
        }
      }, 1500);
    } catch (e) {
      setPreviewingRingtone(null);
      console.warn('Could not preview ringtone:', e);
    }
  };

  const stopRingtonePreview = () => {
    if (previewAudioRef.current) {
      if (previewAudioRef.current instanceof AudioContext) {
        if (previewAudioRef.current.state !== 'closed') {
          try { previewAudioRef.current.close(); } catch (e) {}
        }
      } else if (previewAudioRef.current.pause) {
        previewAudioRef.current.pause();
      }
      previewAudioRef.current = null;
    }
    setPreviewingRingtone(null);
  };

  // Handle custom ringtone file upload
  const handleRingtoneUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('audio/')) {
      toast.error('Please select an audio file (MP3, WAV, etc.)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Audio file must be less than 5MB');
      return;
    }
    
    setUploadingRingtone(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      setCustomRingtone(e.target.result);
      setCustomRingtoneName(file.name);
      setSelectedRingtone('custom');
      setUploadingRingtone(false);
      toast.success(`Loaded "${file.name}" — click Save Audio Settings to apply`);
    };
    reader.onerror = () => {
      toast.error('Failed to read audio file');
      setUploadingRingtone(false);
    };
    reader.readAsDataURL(file);
  };

  const handleDeleteCustomRingtone = async () => {
    try {
      await axios.delete(`${API_URL}/api/auth/ringtone`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomRingtone(null);
      setCustomRingtoneName('');
      setSelectedRingtone('classic');
      localStorage.removeItem('customRingtone');
      localStorage.setItem('preferredRingtone', 'classic');
      await refreshUser();
      toast.success('Custom ringtone removed, reset to Classic Ring');
    } catch (error) {
      toast.error('Failed to remove custom ringtone');
    }
  };

  // Role display
  const getRoleBadge = () => {
    const roleMap = {
      developer: { label: 'Developer', variant: 'destructive', description: 'Full system access' },
      customer_admin: { label: 'Customer Admin', variant: 'default', description: 'Company administrator' },
      phone_admin: { label: 'Phone Admin', variant: 'secondary', description: 'Phone system management' },
      standard: { label: 'Standard User', variant: 'outline', description: 'Basic access' }
    };
    return roleMap[user?.role] || roleMap.standard;
  };

  const roleBadge = getRoleBadge();

  // Toggle dialer enabled/disabled (DND)
  const handleDialerToggle = async (enabled) => {
    setDialerToggleLoading(true);
    try {
      await fetch(`${API_URL}/api/auth/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ dialer_enabled: enabled })
      });
      setDialerEnabled(enabled);
      await refreshUser();
      toast.success(enabled ? 'Dialer enabled — you will receive calls' : 'Dialer disabled — you will not receive calls');
    } catch (error) {
      toast.error('Failed to update dialer setting');
    } finally {
      setDialerToggleLoading(false);
    }
  };

  // Update profile (name only — no email)
  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setProfileLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ name, email })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to update profile');
      await refreshUser();
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to update profile');
    } finally {
      setProfileLoading(false);
    }
  };

  // Change password
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { toast.error('New passwords do not match'); return; }
    if (newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setPasswordLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to change password');
      toast.success('Password changed successfully');
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (error) {
      toast.error(error.message || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div className="p-8 max-w-4xl mx-auto" data-testid="profile-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-heading font-bold text-foreground">My Settings</h1>
        <p className="text-muted-foreground mt-1">Manage your profile, audio, and preferences</p>
      </div>

      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList className="bg-secondary/50 p-1">
          <TabsTrigger value="profile" data-testid="tab-profile" className="gap-2">
            <User className="w-4 h-4" /> Profile
          </TabsTrigger>
          <TabsTrigger value="account" data-testid="tab-account" className="gap-2">
            <Shield className="w-4 h-4" /> Account
          </TabsTrigger>
          <TabsTrigger value="audio" data-testid="tab-audio" className="gap-2">
            <Volume2 className="w-4 h-4" /> Audio
          </TabsTrigger>
          <TabsTrigger value="preferences" data-testid="tab-preferences" className="gap-2">
            <Home className="w-4 h-4" /> Preferences
          </TabsTrigger>
          <TabsTrigger value="downloads" data-testid="tab-downloads" className="gap-2">
            <Download className="w-4 h-4" /> Downloads
          </TabsTrigger>
        </TabsList>

        {/* ===== PROFILE TAB ===== */}
        <TabsContent value="profile" className="space-y-6">
          <Card className="border-border/50" data-testid="profile-tab-card">
            <CardHeader>
              <CardTitle>Profile</CardTitle>
              <CardDescription>Your photo and display name</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-start gap-8">
                {/* Image */}
                <div className="flex flex-col items-center gap-3">
                  <div className="relative">
                    <div className="w-28 h-28 rounded-full bg-secondary/50 border-2 border-border flex items-center justify-center overflow-hidden">
                      {profileImage ? (
                        <img src={profileImage} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-14 h-14 text-muted-foreground" />
                      )}
                    </div>
                    {uploadingImage && (
                      <div className="absolute inset-0 flex items-center justify-center bg-background/80 rounded-full">
                        <Loader2 className="w-6 h-6 animate-spin" />
                      </div>
                    )}
                  </div>
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageSelect} className="hidden" />
                  <Button 
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage}
                    variant="outline"
                    size="sm"
                    data-testid="upload-image-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {profileImage ? 'Change' : 'Upload'}
                  </Button>
                  {profileImage && (
                    <Button 
                      onClick={handleDeleteImage}
                      disabled={uploadingImage}
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                      data-testid="delete-image-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Remove
                    </Button>
                  )}
                  <p className="text-xs text-muted-foreground text-center">Max 2MB<br/>JPG, PNG, or GIF</p>
                </div>

                {/* Name form */}
                <form onSubmit={handleProfileUpdate} className="flex-1 space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Display Name</Label>
                    <Input
                      id="name"
                      type="text"
                      placeholder="Enter your name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      data-testid="profile-name-input"
                    />
                  </div>
                  <div className="flex justify-end">
                    <Button type="submit" disabled={profileLoading} data-testid="save-profile-btn">
                      {profileLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Save
                    </Button>
                  </div>
                </form>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== ACCOUNT TAB ===== */}
        <TabsContent value="account" className="space-y-6">
          {/* Account Overview */}
          <Card className="border-border/50" data-testid="account-overview-card">
            <CardHeader>
              <CardTitle>Account Overview</CardTitle>
              <CardDescription>Your account information</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Phone className="w-4 h-4" />
                    Phone Number (SIP)
                  </div>
                  <p className="font-mono text-lg" data-testid="user-phone">{user?.phone_number}</p>
                </div>
                {userExtension && (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Phone className="w-4 h-4" />
                      Extension
                    </div>
                    <p className="font-mono text-lg" data-testid="user-extension">x{userExtension}</p>
                  </div>
                )}
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Shield className="w-4 h-4" />
                    Role
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={roleBadge.variant} data-testid="user-role-badge">{roleBadge.label}</Badge>
                    <span className="text-sm text-muted-foreground">{roleBadge.description}</span>
                  </div>
                </div>
                {user?.company_name && (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Building2 className="w-4 h-4" />
                      Company
                    </div>
                    <p className="text-lg" data-testid="user-company">{user.company_name}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Change Password */}
          <Card className="border-border/50" data-testid="password-change-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="w-5 h-5" />
                Change Password
              </CardTitle>
              <CardDescription>
                Update your password. This will also update your SIP password for softphone connections.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="current-password">Current Password</Label>
                  <div className="relative">
                    <Input
                      id="current-password"
                      type={showCurrentPassword ? 'text' : 'password'}
                      placeholder="Enter current password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="pr-10"
                      data-testid="current-password-input"
                    />
                    <button type="button" onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Separator />
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="new-password">New Password</Label>
                    <div className="relative">
                      <Input
                        id="new-password"
                        type={showNewPassword ? 'text' : 'password'}
                        placeholder="Enter new password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="pr-10"
                        data-testid="new-password-input"
                      />
                      <button type="button" onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                        {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Confirm New Password</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>
                {newPassword && confirmPassword && newPassword !== confirmPassword && (
                  <p className="text-sm text-destructive">Passwords do not match</p>
                )}
                <div className="flex justify-end">
                  <Button type="submit" variant="outline"
                    disabled={passwordLoading || !currentPassword || !newPassword || !confirmPassword}
                    data-testid="change-password-btn">
                    {passwordLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Key className="w-4 h-4 mr-2" />}
                    Change Password
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Phone Credentials */}
          <Card className="border-border/50" data-testid="phone-credentials-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Phone className="w-5 h-5" />
                Phone Credentials
              </CardTitle>
              <CardDescription>
                Manage your SIP phone credentials for the softphone.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full ${getSavedSipCredentials(user?.phone_number) ? 'bg-emerald-500' : 'bg-zinc-500'}`} />
                  <div>
                    <p className="text-sm font-medium">Remember credentials on this device</p>
                    <p className="text-xs text-muted-foreground">
                      {getSavedSipCredentials(user?.phone_number) 
                        ? 'Your SIP credentials are saved locally for automatic login' 
                        : 'Your credentials are not saved locally'}
                    </p>
                  </div>
                </div>
                {getSavedSipCredentials(user?.phone_number) && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      clearSavedSipCredentials();
                      toast.success('Saved credentials cleared');
                      // Force re-render
                      window.location.reload();
                    }}
                    data-testid="clear-saved-creds-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Clear Saved
                  </Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                To save your credentials, enter your SIP password below and check "Remember credentials".
                Only use this on personal or trusted devices.
              </p>
              <Separator />
              <SaveCredentialsForm user={user} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== AUDIO TAB ===== */}
        <TabsContent value="audio" className="space-y-6">
          {/* Devices */}
          <Card className="border-border/50" data-testid="audio-devices-card">
            <CardHeader>
              <CardTitle>Speaker & Microphone</CardTitle>
              <CardDescription>Configure your audio input and output devices</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-6">
                {/* Microphone */}
                <div className="space-y-3">
                  <Label className="flex items-center gap-2">
                    <Mic className="w-4 h-4" /> Microphone
                  </Label>
                  <Select value={selectedMic} onValueChange={setSelectedMic}>
                    <SelectTrigger data-testid="mic-select"><SelectValue placeholder="Select microphone" /></SelectTrigger>
                    <SelectContent>
                      {audioDevices.inputs.map(device => (
                        <SelectItem key={device.deviceId} value={device.deviceId}>{device.label}</SelectItem>
                      ))}
                      {audioDevices.inputs.length === 0 && <SelectItem value="none" disabled>No microphones found</SelectItem>}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" onClick={testMicrophone} disabled={testingAudio || !selectedMic} className="w-full" data-testid="test-mic-btn">
                    {testingAudio ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mic className="w-4 h-4 mr-2" />}
                    Test Microphone
                  </Button>
                </div>
                {/* Speaker */}
                <div className="space-y-3">
                  <Label className="flex items-center gap-2">
                    <Volume2 className="w-4 h-4" /> Speaker
                  </Label>
                  <Select value={selectedSpeaker} onValueChange={setSelectedSpeaker}>
                    <SelectTrigger data-testid="speaker-select"><SelectValue placeholder="Select speaker" /></SelectTrigger>
                    <SelectContent>
                      {audioDevices.outputs.map(device => (
                        <SelectItem key={device.deviceId} value={device.deviceId}>{device.label}</SelectItem>
                      ))}
                      {audioDevices.outputs.length === 0 && <SelectItem value="none" disabled>No speakers found</SelectItem>}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" onClick={testSpeaker} disabled={testingAudio} className="w-full" data-testid="test-speaker-btn">
                    {testingAudio ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Volume2 className="w-4 h-4 mr-2" />}
                    Test Speaker
                  </Button>
                </div>
              </div>
              <div className="flex items-center justify-between pt-2">
                <Button variant="ghost" size="sm" onClick={enumerateAudioDevices} disabled={loadingDevices}>
                  <RefreshCw className={`w-4 h-4 mr-2 ${loadingDevices ? 'animate-spin' : ''}`} />
                  Refresh Devices
                </Button>
                <Button onClick={saveAudioPreferences} size="sm" data-testid="save-devices-btn">
                  <Save className="w-4 h-4 mr-2" />
                  Save Audio Settings
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Ringtone */}
          <Card className="border-border/50" data-testid="ringtone-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Music className="w-5 h-5" /> Ringtone
              </CardTitle>
              <CardDescription>Choose the ringtone for incoming calls. Your selection syncs across all devices.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                {RINGTONE_OPTIONS.map((tone) => (
                  <div
                    key={tone.id}
                    className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedRingtone === tone.id
                        ? 'border-primary bg-primary/5'
                        : 'border-border/50 hover:border-border hover:bg-secondary/30'
                    }`}
                    onClick={() => setSelectedRingtone(tone.id)}
                    data-testid={`ringtone-${tone.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        selectedRingtone === tone.id ? 'border-primary' : 'border-muted-foreground/40'
                      }`}>
                        {selectedRingtone === tone.id && <div className="w-2 h-2 rounded-full bg-primary" />}
                      </div>
                      <span className="text-sm font-medium">{tone.label}</span>
                    </div>
                    {tone.id !== 'none' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (previewingRingtone === tone.id) {
                            stopRingtonePreview();
                          } else {
                            previewRingtone(tone);
                          }
                        }}
                        className="h-8 w-8 p-0"
                        data-testid={`preview-ringtone-${tone.id}`}
                      >
                        {previewingRingtone === tone.id ? (
                          <Square className="w-3.5 h-3.5" />
                        ) : (
                          <Play className="w-3.5 h-3.5" />
                        )}
                      </Button>
                    )}
                  </div>
                ))}

                {/* Custom Ringtone */}
                <div
                  className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedRingtone === 'custom'
                      ? 'border-primary bg-primary/5'
                      : 'border-border/50 hover:border-border hover:bg-secondary/30'
                  }`}
                  onClick={() => { if (customRingtone) setSelectedRingtone('custom'); }}
                  data-testid="ringtone-custom"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      selectedRingtone === 'custom' ? 'border-primary' : 'border-muted-foreground/40'
                    }`}>
                      {selectedRingtone === 'custom' && <div className="w-2 h-2 rounded-full bg-primary" />}
                    </div>
                    <div>
                      <span className="text-sm font-medium">Custom Ringtone</span>
                      {customRingtone && customRingtoneName && (
                        <span className="text-xs text-muted-foreground ml-2">({customRingtoneName})</span>
                      )}
                      {customRingtone && !customRingtoneName && (
                        <span className="text-xs text-muted-foreground ml-2">(uploaded)</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {customRingtone && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (previewingRingtone === 'custom') {
                              stopRingtonePreview();
                            } else {
                              previewRingtone({ id: 'custom' });
                            }
                          }}
                          className="h-8 w-8 p-0"
                          data-testid="preview-ringtone-custom"
                        >
                          {previewingRingtone === 'custom' ? (
                            <Square className="w-3.5 h-3.5" />
                          ) : (
                            <Play className="w-3.5 h-3.5" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteCustomRingtone();
                          }}
                          className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-500/10"
                          data-testid="delete-custom-ringtone"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </>
                    )}
                    <input ref={ringtoneFileRef} type="file" accept="audio/*" onChange={handleRingtoneUpload} className="hidden" />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        ringtoneFileRef.current?.click();
                      }}
                      disabled={uploadingRingtone}
                      className="h-8 px-2 text-xs"
                      data-testid="upload-ringtone-btn"
                    >
                      {uploadingRingtone ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <>
                          <Upload className="w-3.5 h-3.5 mr-1" />
                          {customRingtone ? 'Replace' : 'Upload'}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">Upload MP3, WAV, or OGG (max 5MB)</p>
            </CardContent>
          </Card>

          {/* Call Notifications */}
          {pushSupported && (
            <Card className="border-border/50" data-testid="notifications-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="w-5 h-5" /> Call Notifications
                </CardTitle>
                <CardDescription>Receive browser notifications for incoming calls</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between p-4 rounded-lg bg-secondary/30">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${pushSubscribed ? 'bg-green-500/20' : 'bg-secondary'}`}>
                      <Bell className={`w-5 h-5 ${pushSubscribed ? 'text-green-500' : 'text-muted-foreground'}`} />
                    </div>
                    <div>
                      <p className="font-medium">Push Notifications</p>
                      <p className="text-sm text-muted-foreground">
                        {pushPermission === 'denied' 
                          ? 'Blocked - enable in browser settings'
                          : pushSubscribed 
                            ? 'You will be notified of incoming calls' 
                            : 'Get alerts for incoming calls'}
                      </p>
                    </div>
                  </div>
                  {pushPermission === 'denied' ? (
                    <span className="text-sm text-destructive">Blocked</span>
                  ) : (
                    <Button 
                      variant={pushSubscribed ? "outline" : "default"}
                      onClick={pushSubscribed ? unsubscribePush : subscribePush}
                      disabled={pushLoading}
                    >
                      {pushLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : pushSubscribed ? 'Disable' : 'Enable'}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Save Audio */}
          <div className="flex justify-end">
            <Button onClick={saveAudioPreferences} data-testid="save-audio-btn">
              <Save className="w-4 h-4 mr-2" />
              Save Audio Settings
            </Button>
          </div>
        </TabsContent>

        {/* ===== PREFERENCES TAB ===== */}
        <TabsContent value="preferences" className="space-y-6">
          <Card className="border-border/50" data-testid="preferences-card">
            <CardHeader>
              <CardTitle>Preferences</CardTitle>
              <CardDescription>Customize your CONNECT experience</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Dialer / Do Not Disturb */}
              <div>
                <h4 className="text-sm font-medium mb-4">Softphone</h4>
                <div className="flex items-center justify-between p-4 rounded-lg bg-secondary/30">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${dialerEnabled ? 'bg-emerald-500/20' : 'bg-red-500/20'}`}>
                      {dialerEnabled ? (
                        <Phone className={`w-5 h-5 text-emerald-500`} />
                      ) : (
                        <PhoneOff className={`w-5 h-5 text-red-500`} />
                      )}
                    </div>
                    <div>
                      <p className="font-medium">Receive Calls</p>
                      <p className="text-sm text-muted-foreground">
                        {dialerEnabled
                          ? 'Your softphone is active and will ring for incoming calls'
                          : 'Your softphone is off — you can still monitor queues but will not receive calls'}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={dialerEnabled}
                    onCheckedChange={handleDialerToggle}
                    disabled={dialerToggleLoading}
                    data-testid="dialer-enabled-toggle"
                  />
                </div>
              </div>

              <Separator />

              {/* Contact Filter */}
              <div>
                <h4 className="text-sm font-medium mb-4">Contact List</h4>
                <div className="space-y-3">
                  <Label className="flex items-center gap-2">
                    <User className="w-4 h-4" /> Default Contact View
                  </Label>
                  <Select value={defaultContactFilter} onValueChange={setDefaultContactFilter}>
                    <SelectTrigger className="w-full sm:w-64" data-testid="contact-filter-select">
                      <SelectValue placeholder="Select default view" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Contacts</SelectItem>
                      <SelectItem value="internal">Internal Only</SelectItem>
                      <SelectItem value="external">External Only</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    This will be the default filter when you open the Dialer or Contacts page
                  </p>
                </div>
              </div>

              <Separator />

              {/* Landing Page */}
              <div>
                <h4 className="text-sm font-medium mb-4">Landing Page</h4>
                <div className="space-y-3">
                  <Label className="flex items-center gap-2">
                    <Home className="w-4 h-4" /> Default Page on Login
                  </Label>
                  <Select value={defaultLandingPage} onValueChange={setDefaultLandingPage}>
                    <SelectTrigger className="w-full sm:w-64" data-testid="landing-page-select">
                      <SelectValue placeholder="Select default page" />
                    </SelectTrigger>
                    <SelectContent>
                      {LANDING_PAGE_OPTIONS
                        .filter(option => {
                          const hasAccess = hasFeatureAccess(option.backendId);
                          const isPlanRestricted = isFeatureRestrictedByPlan(option.backendId);
                          return hasAccess || isPlanRestricted;
                        })
                        .map(option => (
                          <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                        ))
                      }
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    This page will open automatically when you log in
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button onClick={savePreferences} data-testid="save-preferences-btn">
              <Save className="w-4 h-4 mr-2" />
              Save Preferences
            </Button>
          </div>
        </TabsContent>

        {/* ===== DOWNLOADS TAB ===== */}
        <TabsContent value="downloads" className="space-y-6">
          <Card className="border-border/50" data-testid="app-install-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Download className="w-5 h-5" /> Desktop App
              </CardTitle>
              <CardDescription>Download the CONNECT desktop app for the best experience</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {isElectron && (
                <div className="flex items-center gap-3 p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                  <div className="p-2 rounded-full bg-green-500/20">
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                  </div>
                  <div>
                    <p className="font-medium text-green-500">Desktop App Active</p>
                    <p className="text-sm text-muted-foreground">You're using the CONNECT desktop application</p>
                  </div>
                </div>
              )}
              {!isElectron && (
                <div className="flex items-center gap-3 p-4 rounded-lg bg-secondary/30">
                  <div className="p-2 rounded-full bg-blue-500/20">
                    <Monitor className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="font-medium">Get the Desktop App</p>
                    <p className="text-sm text-muted-foreground">Auto-start on login, system tray, and instant call notifications</p>
                  </div>
                </div>
              )}
              {(desktopVersions.windows?.download_url || desktopVersions.mac?.download_url) && (
                <div className="flex flex-wrap gap-3 pl-4">
                  {desktopVersions.windows?.download_url && (
                    <Button
                      variant="default"
                      onClick={() => {
                        const url = desktopVersions.windows.download_url;
                        window.open(url.startsWith('http') ? url : `${API_URL}${url}`, '_blank');
                      }}
                      className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
                      data-testid="download-windows-btn"
                    >
                      <Monitor className="w-4 h-4" />
                      {isElectron ? 'Re-install for Windows' : 'Download for Windows'}
                      {desktopVersions.windows.version && (
                        <Badge variant="secondary" className="ml-1 text-xs bg-blue-500/30">v{desktopVersions.windows.version}</Badge>
                      )}
                    </Button>
                  )}
                  {desktopVersions.mac?.download_url && desktopVersions.mac.release_notes !== 'Coming soon' && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        const url = desktopVersions.mac.download_url;
                        window.open(url.startsWith('http') ? url : `${API_URL}${url}`, '_blank');
                      }}
                      className="flex items-center gap-2"
                      data-testid="download-mac-btn"
                    >
                      <Apple className="w-4 h-4" />
                      {isElectron ? 'Re-install for Mac' : 'Download for Mac'}
                      {desktopVersions.mac.version && (
                        <Badge variant="secondary" className="ml-1 text-xs">v{desktopVersions.mac.version}</Badge>
                      )}
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
